import { useEffect, useRef, useState } from "react";

// §9.17/D139 — Studio-Vorschau-Canvas: zeichnet den aktuellen Frame über ein Schachbrett-Karo
// (Transparenz sichtbar). Reines Rendering — Playback/Playhead steuert StudioShell. Zusätzlich:
// SHIFT halten → Vergleichslupe (Split: links Original = vor Effekten, rechts Editiert; die Naht
// liegt am Cursor). „Original" wird nur übergeben, wenn Effekte aktiv sind (sonst gäbe es nichts
// zu vergleichen).
//
// Ratio: das Canvas behält sein Bitmap-Seitenverhältnis über `width/height:auto` + `max-h/max-w-full`
// in einem FLEX-Center-Container (definite Höhe) — `h-full`/`height:100%` auf einem Grid-Item löst
// gegen einen auto-Track NICHT auf und ließ das Canvas überlaufen (abgeschnitten). Da die Canvas-Box
// exakt das skalierte Bild ist (kein internes Letterbox), ist die Lupen-Mathe direkt (u=mx/breite).

const CHECKER = 12; // Karo-Kachelgröße (px)
const LOUPE = 200; // Lupen-Durchmesser (CSS-px)
const ZOOM = 2.5; // Lupen-Vergrößerung

function paintChecker(ctx: CanvasRenderingContext2D, w: number, h: number) {
  for (let y = 0; y < h; y += CHECKER) {
    for (let x = 0; x < w; x += CHECKER) {
      const dark = ((x / CHECKER) | 0) % 2 === ((y / CHECKER) | 0) % 2;
      ctx.fillStyle = dark ? "#2a2a31" : "#3a3a44";
      ctx.fillRect(x, y, CHECKER, CHECKER);
    }
  }
}

/** ImageData in ein (wiederverwendetes) Offscreen-Canvas legen → als drawImage-Quelle nutzbar. */
function toOffscreen(ref: React.MutableRefObject<HTMLCanvasElement | null>, img: ImageData): HTMLCanvasElement {
  let off = ref.current;
  if (!off) {
    off = document.createElement("canvas");
    ref.current = off;
  }
  off.width = img.width;
  off.height = img.height;
  off.getContext("2d")?.putImageData(img, 0, 0);
  return off;
}

export function StudioCanvas({ frame, original }: { frame: ImageData | null; original?: ImageData | null }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const loupeRef = useRef<HTMLCanvasElement>(null);
  const offEditRef = useRef<HTMLCanvasElement | null>(null);
  const offOrigRef = useRef<HTMLCanvasElement | null>(null);
  const [shiftDown, setShiftDown] = useState(false);
  // Cursor: clientX/Y (fürs Sampling gg. canvas-rect) + wx/wy (rel. Wrapper, fürs Overlay-Layout).
  const [cursor, setCursor] = useState<{ x: number; y: number; wx: number; wy: number } | null>(null);

  // Haupt-Frame zeichnen + editiertes Offscreen pflegen (für die Lupe).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (!frame) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    if (canvas.width !== frame.width || canvas.height !== frame.height) {
      canvas.width = frame.width;
      canvas.height = frame.height;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    paintChecker(ctx, canvas.width, canvas.height);
    ctx.drawImage(toOffscreen(offEditRef, frame), 0, 0);
  }, [frame]);

  // Original-Offscreen pflegen (nur für die Lupe).
  useEffect(() => {
    if (original) toOffscreen(offOrigRef, original);
  }, [original]);

  // Shift global tracken (Lupe an/aus auch ohne Mausbewegung; bei Fokusverlust zurücksetzen).
  useEffect(() => {
    const down = (e: KeyboardEvent) => e.key === "Shift" && setShiftDown(true);
    const up = (e: KeyboardEvent) => e.key === "Shift" && setShiftDown(false);
    const blur = () => setShiftDown(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, []);

  // Lupe sobald Shift gehalten wird UND ein Frame da ist — UNABHÄNGIG von Cursor/Effekten
  // (sonst „Shift macht nichts", wenn die Maus seit dem Druck nicht bewegt wurde oder kein
  // Effekt aktiv ist). Split (Original|Editiert) nur, wenn es einen echten Unterschied gibt.
  const hasSplit = !!original && original !== frame;
  const compareActive = shiftDown && !!frame;

  useEffect(() => {
    if (!compareActive || !frame) return;
    const main = canvasRef.current;
    const loupe = loupeRef.current;
    const offEdit = offEditRef.current;
    if (!main || !loupe || !offEdit) return;
    const lctx = loupe.getContext("2d");
    if (!lctx) return;
    const rect = main.getBoundingClientRect();
    // Cursor → relative Position; ohne (Maus nicht bewegt/außerhalb) → Bildmitte.
    let u = 0.5;
    let v = 0.5;
    if (cursor) {
      const mx = cursor.x - rect.left;
      const my = cursor.y - rect.top;
      if (mx >= 0 && my >= 0 && mx <= rect.width && my <= rect.height) {
        u = mx / rect.width; // Canvas-Box == skaliertes Bild (kein Letterbox)
        v = my / rect.height;
      }
    }
    const winX = LOUPE / ZOOM / rect.width; // Fensterbreite als Bildanteil
    const winY = LOUPE / ZOOM / rect.height;

    loupe.width = LOUPE;
    loupe.height = LOUPE;
    lctx.clearRect(0, 0, LOUPE, LOUPE);
    lctx.save();
    lctx.beginPath();
    lctx.arc(LOUPE / 2, LOUPE / 2, LOUPE / 2, 0, Math.PI * 2);
    lctx.clip();
    paintChecker(lctx, LOUPE, LOUPE);
    lctx.imageSmoothingEnabled = false; // Pixel-Treue im Zoom

    if (hasSplit && offOrigRef.current && original) {
      // Links ORIGINAL [u-winX/2 .. u], rechts EDITIERT [u .. u+winX/2], Naht am Cursor.
      const o = offOrigRef.current;
      lctx.drawImage(o, (u - winX / 2) * original.width, (v - winY / 2) * original.height, (winX / 2) * original.width, winY * original.height, 0, 0, LOUPE / 2, LOUPE);
      lctx.drawImage(offEdit, u * frame.width, (v - winY / 2) * frame.height, (winX / 2) * frame.width, winY * frame.height, LOUPE / 2, 0, LOUPE / 2, LOUPE);
      lctx.restore();
      lctx.strokeStyle = "rgba(255,255,255,0.9)";
      lctx.lineWidth = 1;
      lctx.beginPath();
      lctx.moveTo(LOUPE / 2, 0);
      lctx.lineTo(LOUPE / 2, LOUPE);
      lctx.stroke();
    } else {
      // Kein Unterschied → reines Zoom-Glas (volles Fenster [u-winX/2 .. u+winX/2]).
      lctx.drawImage(offEdit, (u - winX / 2) * frame.width, (v - winY / 2) * frame.height, winX * frame.width, winY * frame.height, 0, 0, LOUPE, LOUPE);
      lctx.restore();
    }
    // Ring.
    lctx.beginPath();
    lctx.arc(LOUPE / 2, LOUPE / 2, LOUPE / 2 - 1, 0, Math.PI * 2);
    lctx.strokeStyle = "rgba(0,0,0,0.55)";
    lctx.lineWidth = 2;
    lctx.stroke();
  }, [compareActive, hasSplit, cursor, frame, original]);

  return (
    <div
      ref={wrapRef}
      className="relative flex h-full w-full items-center justify-center overflow-hidden bg-surface-2 p-4"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setCursor({ x: e.clientX, y: e.clientY, wx: e.clientX - r.left, wy: e.clientY - r.top });
      }}
      onMouseLeave={() => setCursor(null)}
    >
      {/* width/height:auto + max-* wahrt das Bitmap-Seitenverhältnis im Flex-Center-Container. */}
      <canvas
        ref={canvasRef}
        className="block max-h-full max-w-full"
        style={{ imageRendering: "pixelated", width: "auto", height: "auto" }}
      />

      {compareActive && (
        <>
          <canvas
            ref={loupeRef}
            width={LOUPE}
            height={LOUPE}
            className="pointer-events-none absolute z-20 rounded-full shadow-lg"
            style={
              cursor
                ? { left: cursor.wx - LOUPE / 2, top: cursor.wy - LOUPE / 2, width: LOUPE, height: LOUPE }
                : { left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: LOUPE, height: LOUPE }
            }
          />
          {hasSplit && (
            <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">
              Original │ Editiert
            </div>
          )}
        </>
      )}
      {frame && !compareActive && (
        <div className="pointer-events-none absolute bottom-2 right-2 z-10 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white/80">
          ⇧ Shift halten: Lupe / Original-Vergleich
        </div>
      )}
    </div>
  );
}
